/**
 * CURVANA — Animations
 *
 * Deux effets seulement, volontairement :
 *   1. Révélation au scroll  — fondu + légère montée, une seule fois
 *   2. Comptage des chiffres — attire l'œil sur la preuve chiffrée
 *
 * Multiplier les effets fait "template". Ces deux-là servent le propos :
 * le premier donne un rythme à la lecture, le second met en valeur ce qui
 * doit convaincre.
 *
 * Tout est ignoré si la personne a demandé de réduire les animations, et
 * si le JS ne s'exécute pas, le contenu reste visible (l'opacité initiale
 * n'est posée que par ce script).
 */

(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------------
     0. Vidéos décoratives en boucle

     Traité avant la sortie ci-dessous : c'est justement quand la personne
     demande moins d'animation qu'il faut arrêter la boucle, pas l'ignorer.
     L'affiche prend alors le relais, et le poster de la balise s'affiche
     tout seul dès que la lecture s'arrête au premier frame.
     --------------------------------------------------------------------- */

  const loops = document.querySelectorAll('video[data-cv-loop]');

  if (loops.length) {
    if (reduced.matches) {
      loops.forEach((v) => {
        v.removeAttribute('autoplay');
        v.pause();
      });
    } else if ('IntersectionObserver' in window) {
      // Une boucle qui tourne hors écran ne fait que consommer de la batterie
      // et de la data. On la met en pause dès qu'elle sort du champ.
      const loopObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const v = entry.target;
            if (entry.isIntersecting) {
              // play() renvoie une promesse rejetée si le navigateur refuse
              // la lecture auto. Sans ce catch, la console se remplit d'erreurs
              // pour une vidéo qui n'est de toute façon que décorative.
              const played = v.play();
              if (played) played.catch(() => {});
            } else {
              v.pause();
            }
          });
        },
        { threshold: 0.1 }
      );

      loops.forEach((v) => loopObserver.observe(v));
    }
  }

  if (reduced.matches) return;

  /* ---------------------------------------------------------------------
     1. Révélation au scroll
     --------------------------------------------------------------------- */

  const revealTargets = document.querySelectorAll('[data-cv-reveal]');

  if (revealTargets.length && 'IntersectionObserver' in window) {
    // On ne pose la classe d'état initial qu'ici : sans JS, rien n'est masqué.
    revealTargets.forEach((el) => el.classList.add('cv-reveal'));

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          // Décalage en cascade entre frères, pour un enchaînement plutôt
          // qu'un bloc qui apparaît d'un coup.
          const delay = Number(el.dataset.cvReveal) || 0;
          el.style.transitionDelay = `${delay * 80}ms`;
          el.classList.add('cv-reveal--in');
          revealObserver.unobserve(el);
        });
      },
      // Déclenche un peu avant l'entrée réelle : l'élément est déjà en
      // place quand l'œil l'atteint.
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    revealTargets.forEach((el) => revealObserver.observe(el));
  }

  /* ---------------------------------------------------------------------
     2. Comptage des chiffres
     --------------------------------------------------------------------- */

  const countTargets = document.querySelectorAll('[data-cv-count]');

  if (countTargets.length && 'IntersectionObserver' in window) {
    // Sépare la partie numérique de sa mise en forme : "30,000" doit
    // rester "30,000" et "4.8" garder sa décimale.
    const parse = (raw) => {
      const cleaned = raw.replace(/[^\d.,]/g, '');
      const decimalSep = cleaned.lastIndexOf('.') > cleaned.lastIndexOf(',') ? '.' : ',';
      const normalized =
        decimalSep === '.'
          ? cleaned.replace(/,/g, '')
          : cleaned.replace(/\./g, '').replace(',', '.');
      const value = parseFloat(normalized);
      if (Number.isNaN(value)) return null;
      const decimals = (normalized.split('.')[1] || '').length;
      const grouped = /[.,]\d{3}\b/.test(cleaned);
      return { value, decimals, grouped, decimalSep };
    };

    const format = (n, spec) => {
      let out = n.toFixed(spec.decimals);
      if (spec.grouped) {
        const [int, dec] = out.split('.');
        out = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (dec ? '.' + dec : '');
      }
      if (spec.decimalSep === ',') out = out.replace('.', ',');
      return out;
    };

    const countObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          countObserver.unobserve(el);

          const spec = parse(el.textContent.trim());
          if (!spec) return;

          const duration = 1100;
          const start = performance.now();

          const step = (now) => {
            const t = Math.min((now - start) / duration, 1);
            // easeOutExpo : démarre vite, se pose doucement sur la valeur.
            const eased = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
            el.textContent = format(spec.value * eased, spec);
            if (t < 1) requestAnimationFrame(step);
          };

          requestAnimationFrame(step);
        });
      },
      { threshold: 0.5 }
    );

    countTargets.forEach((el) => countObserver.observe(el));
  }
})();
